import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Modal, Input, StatCard } from './admin/shared';
import OverviewTab from './admin/OverviewTab';
import OrganizationsTab from './admin/OrganizationsTab';
import UsersTab from './admin/UsersTab';
import TestAccountsTab from './admin/TestAccountsTab';
import PaymentsTab from './admin/PaymentsTab';
import SchedulerTab from './admin/SchedulerTab';
import SubscriptionsTab from './admin/SubscriptionsTab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/v2`;

function SystemAdminPanel() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [users, setUsers] = useState([]);
  const [testUsers, setTestUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState(null);
  
  // Modals
  const [showCreateOrg, setShowCreateOrg] = useState(false);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showEditOrg, setShowEditOrg] = useState(null);
  const [showEditUser, setShowEditUser] = useState(null);
  
  // Form states
  const [newOrg, setNewOrg] = useState({
    name: '', orgnr: '', admin_email: '', admin_name: '', admin_password: '',
    is_test_org: false, expires_days: 7
  });
  const [newUser, setNewUser] = useState({
    name: '', email: '', password: '', org_id: '', role: 'Member',
    is_test_user: false, expires_days: 7
  });
  const [editOrg, setEditOrg] = useState({ name: '', orgnr: '', is_test_org: false, expires_days: 7 });
  const [editUser, setEditUser] = useState({ name: '', email: '', role: '', is_test_user: false, expires_days: 7 });
  
  // Payment states
  const [paymentData, setPaymentData] = useState(null);
  const [showMarkDeletion, setShowMarkDeletion] = useState(null);
  const [deletionForm, setDeletionForm] = useState({ send_email: true, deletion_days: 14, reason: 'manglende betaling' });

  useEffect(() => {
    checkPermissions();
  }, []);

  useEffect(() => {
    if (permissions?.is_system_admin) {
      fetchData();
    }
  }, [permissions, activeTab]);

  const checkPermissions = async () => {
    try {
      const token = localStorage.getItem('systemklart_token');
      const response = await axios.get(`${API}/ops/users/me/permissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPermissions(response.data);
      if (!response.data.is_system_admin) {
        toast.error('Kun for systemadministratorer');
        navigate('/dashboard');
      }
    } catch (error) {
      navigate('/system-login');
    }
  };

  const fetchData = async () => {
    setLoading(true);
    const token = localStorage.getItem('systemklart_token');
    const headers = { Authorization: `Bearer ${token}` };

    try {
      if (activeTab === 'overview' || activeTab === 'organizations') {
        const [statsRes, orgsRes] = await Promise.all([
          axios.get(`${API}/ops/system/stats`, { headers }),
          axios.get(`${API}/ops/system/organizations`, { headers })
        ]);
        setStats(statsRes.data);
        setOrganizations(orgsRes.data.organizations);
      }
      
      if (activeTab === 'users') {
        const usersRes = await axios.get(`${API}/ops/users`, { headers });
        setUsers(usersRes.data.users);
      }
      
      if (activeTab === 'test-accounts') {
        const testRes = await axios.get(`${API}/ops/system/test-users`, { headers });
        setTestUsers(testRes.data.test_users);
      }
      
      if (activeTab === 'payments') {
        const paymentRes = await axios.get(`${API}/ops/system/payment-status`, { headers });
        setPaymentData(paymentRes.data);
      }
      
      if (activeTab === 'subscriptions') {
        // Subscriptions tab fetches its own data
      }
    } catch (error) {
      toast.error('Kunne ikke hente data');
    } finally {
      setLoading(false);
    }
  };

  const createOrganization = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('systemklart_token');
      const expiresAt = newOrg.is_test_org 
        ? new Date(Date.now() + newOrg.expires_days * 24 * 60 * 60 * 1000).toISOString()
        : null;
      
      await axios.post(`${API}/ops/system/organizations`, {
        ...newOrg,
        expires_at: expiresAt
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success('Organisasjon opprettet');
      setShowCreateOrg(false);
      setNewOrg({ name: '', orgnr: '', admin_email: '', admin_name: '', admin_password: '', is_test_org: false, expires_days: 7 });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunne ikke opprette organisasjon');
    }
  };

  const createUser = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('systemklart_token');
      const expiresAt = newUser.is_test_user
        ? new Date(Date.now() + newUser.expires_days * 24 * 60 * 60 * 1000).toISOString()
        : null;
      
      await axios.post(`${API}/ops/system/users`, {
        ...newUser,
        expires_at: expiresAt
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success('Bruker opprettet');
      setShowCreateUser(false);
      setNewUser({ name: '', email: '', password: '', org_id: '', role: 'Member', is_test_user: false, expires_days: 7 });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunne ikke opprette bruker');
    }
  };

  const deleteOrganization = async (orgId, orgName) => {
    try {
      const token = localStorage.getItem('systemklart_token');
      await axios.delete(`${API}/ops/system/organizations/${orgId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${orgName} slettet permanent`);
      setShowDeleteConfirm(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunne ikke slette organisasjon');
    }
  };

  const deleteUserPermanent = async (userId, email) => {
    try {
      const token = localStorage.getItem('systemklart_token');
      await axios.delete(`${API}/ops/system/users/${userId}/permanent`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${email} slettet permanent`);
      setShowDeleteConfirm(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunne ikke slette bruker');
    }
  };

  const cleanupExpired = async () => {
    try {
      const token = localStorage.getItem('systemklart_token');
      const response = await axios.post(`${API}/ops/system/cleanup-expired`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error('Kunne ikke rydde opp');
    }
  };

  const openEditOrg = (org) => {
    setEditOrg({
      id: org.id,
      name: org.name,
      orgnr: org.orgnr,
      is_test_org: org.is_test_org || false,
      expires_days: 7
    });
    setShowEditOrg(true);
  };

  const updateOrganization = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('systemklart_token');
      await axios.patch(`${API}/ops/system/organizations/${editOrg.id}`, {
        name: editOrg.name,
        orgnr: editOrg.orgnr,
        is_test_org: editOrg.is_test_org,
        expires_days: editOrg.is_test_org ? editOrg.expires_days : null
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success('Organisasjon oppdatert');
      setShowEditOrg(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunne ikke oppdatere organisasjon');
    }
  };

  const openEditUser = (user) => {
    setEditUser({
      id: user.id,
      name: user.name,
      email: user.email,
      role: user.role,
      is_test_user: user.is_test_user || false,
      expires_days: 7
    });
    setShowEditUser(true);
  };

  const updateUser = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('systemklart_token');
      await axios.patch(`${API}/ops/system/users/${editUser.id}`, {
        name: editUser.name,
        email: editUser.email,
        role: editUser.role,
        is_test_user: editUser.is_test_user,
        expires_days: editUser.is_test_user ? editUser.expires_days : null
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success('Bruker oppdatert');
      setShowEditUser(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunne ikke oppdatere bruker');
    }
  };

  if (loading && !stats) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <div>
                <h1 className="font-bold text-lg">Systemadministrasjon</h1>
                <p className="text-xs text-gray-400">{permissions?.email}</p>
              </div>
            </div>
            <button
              onClick={() => { localStorage.clear(); navigate('/system-login'); }}
              className="text-sm text-gray-400 hover:text-white"
            >
              Logg ut
            </button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4">
          <nav className="flex gap-1 overflow-x-auto">
            {[
              { id: 'overview', label: 'Oversikt' },
              { id: 'organizations', label: 'Organisasjoner' },
              { id: 'users', label: 'Brukere' },
              { id: 'subscriptions', label: 'Abonnementer' },
              { id: 'test-accounts', label: 'Testkontoer' },
              { id: 'payments', label: 'Betalinger' },
              { id: 'scheduler', label: 'E-postjobber' },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`tab-${tab.id}`}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-red-500 text-white'
                    : 'border-transparent text-gray-400 hover:text-white'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Overview Tab */}
        {activeTab === 'overview' && <OverviewTab stats={stats} />}

        {/* Organizations Tab */}
        {activeTab === 'organizations' && (
          <OrganizationsTab
            organizations={organizations}
            onCreateOrg={() => setShowCreateOrg(true)}
            onEditOrg={openEditOrg}
            onDeleteOrg={(org) => setShowDeleteConfirm({ type: 'org', id: org.id, name: org.name })}
            onRefresh={fetchData}
          />
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <UsersTab
            users={users}
            onCreateUser={() => setShowCreateUser(true)}
            onEditUser={openEditUser}
            onDeleteUser={(user) => setShowDeleteConfirm({ type: 'user', id: user.id, name: user.email })}
            onRefresh={fetchData}
          />
        )}

        {/* Test Accounts Tab */}
        {activeTab === 'test-accounts' && (
          <TestAccountsTab
            testUsers={testUsers}
            onCleanupExpired={cleanupExpired}
            onDeleteUser={(user) => setShowDeleteConfirm({ type: 'user', id: user.id, name: user.email })}
          />
        )}

        {/* Subscriptions Tab */}
        {activeTab === 'subscriptions' && (
          <SubscriptionsTab onRefresh={fetchData} organizations={organizations} />
        )}

        {/* Payments Tab */}
        {activeTab === 'payments' && (
          <PaymentsTab 
            paymentData={paymentData} 
            onRefresh={fetchData}
            showMarkDeletion={showMarkDeletion}
            setShowMarkDeletion={setShowMarkDeletion}
            deletionForm={deletionForm}
            setDeletionForm={setDeletionForm}
          />
        )}

        {/* Scheduler Tab */}
        {activeTab === 'scheduler' && (
          <SchedulerTab />
        )}
      </main>

      {/* Create Organization Modal */}
      {showCreateOrg && (
        <Modal title="Opprett organisasjon" onClose={() => setShowCreateOrg(false)}>
          <form onSubmit={createOrganization} className="space-y-4">
            <Input label="Organisasjonsnavn" value={newOrg.name} onChange={e => setNewOrg({...newOrg, name: e.target.value})} required />
            <Input label="Org.nummer" value={newOrg.orgnr} onChange={e => setNewOrg({...newOrg, orgnr: e.target.value})} required />
            <Input label="Admin e-post" type="email" value={newOrg.admin_email} onChange={e => setNewOrg({...newOrg, admin_email: e.target.value})} required />
            <Input label="Admin navn" value={newOrg.admin_name} onChange={e => setNewOrg({...newOrg, admin_name: e.target.value})} required />
            <Input label="Admin passord" type="password" value={newOrg.admin_password} onChange={e => setNewOrg({...newOrg, admin_password: e.target.value})} required />
            
            <div className="flex items-center gap-3">
              <input type="checkbox" id="test_org" checked={newOrg.is_test_org} onChange={e => setNewOrg({...newOrg, is_test_org: e.target.checked})} className="rounded" />
              <label htmlFor="test_org" className="text-sm">Testkonto (tidsbegrenset)</label>
            </div>
            
            {newOrg.is_test_org && (
              <Input label="Utløper om (dager)" type="number" value={newOrg.expires_days} onChange={e => setNewOrg({...newOrg, expires_days: parseInt(e.target.value)})} min={1} max={365} />
            )}
            
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={() => setShowCreateOrg(false)} className="flex-1 py-2 border border-gray-600 rounded-lg hover:bg-gray-700">Avbryt</button>
              <button type="submit" className="flex-1 py-2 bg-red-600 rounded-lg hover:bg-red-700">Opprett</button>
            </div>
          </form>
        </Modal>
      )}

      {/* Create User Modal */}
      {showCreateUser && (
        <Modal title="Opprett bruker" onClose={() => setShowCreateUser(false)}>
          <form onSubmit={createUser} className="space-y-4">
            <Input label="Navn" value={newUser.name} onChange={e => setNewUser({...newUser, name: e.target.value})} required />
            <Input label="E-post" type="email" value={newUser.email} onChange={e => setNewUser({...newUser, email: e.target.value})} required />
            <Input label="Passord" type="password" value={newUser.password} onChange={e => setNewUser({...newUser, password: e.target.value})} required />
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Organisasjon</label>
              <select
                value={newUser.org_id}
                onChange={e => setNewUser({...newUser, org_id: e.target.value})}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                required
              >
                <option value="">Velg organisasjon</option>
                {organizations.map(org => (
                  <option key={org.id} value={org.id}>{org.name}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Rolle</label>
              <select
                value={newUser.role}
                onChange={e => setNewUser({...newUser, role: e.target.value})}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
              >
                <option value="Member">Medlem</option>
                <option value="PowerUser">Superbruker</option>
                <option value="Admin">Administrator</option>
              </select>
            </div>
            
            <div className="flex items-center gap-3">
              <input type="checkbox" id="test_user" checked={newUser.is_test_user} onChange={e => setNewUser({...newUser, is_test_user: e.target.checked})} className="rounded" />
              <label htmlFor="test_user" className="text-sm">Testbruker (tidsbegrenset)</label>
            </div>
            
            {newUser.is_test_user && (
              <Input label="Utløper om (dager)" type="number" value={newUser.expires_days} onChange={e => setNewUser({...newUser, expires_days: parseInt(e.target.value)})} min={1} max={365} />
            )}
            
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={() => setShowCreateUser(false)} className="flex-1 py-2 border border-gray-600 rounded-lg hover:bg-gray-700">Avbryt</button>
              <button type="submit" className="flex-1 py-2 bg-red-600 rounded-lg hover:bg-red-700">Opprett</button>
            </div>
          </form>
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <Modal title="Bekreft sletting" onClose={() => setShowDeleteConfirm(null)}>
          <div className="space-y-4">
            <p className="text-gray-300">
              Er du sikker på at du vil slette <strong>{showDeleteConfirm.name}</strong> permanent?
            </p>
            <p className="text-sm text-red-400">
              {showDeleteConfirm.type === 'org' 
                ? 'Dette vil slette organisasjonen og ALLE brukere, oppgaver, avvik og dokumenter tilknyttet den.'
                : 'Denne handlingen kan ikke angres.'}
            </p>
            <div className="flex gap-3 pt-2">
              <button onClick={() => setShowDeleteConfirm(null)} className="flex-1 py-2 border border-gray-600 rounded-lg hover:bg-gray-700">
                Avbryt
              </button>
              <button
                onClick={() => {
                  if (showDeleteConfirm.type === 'org') {
                    deleteOrganization(showDeleteConfirm.id, showDeleteConfirm.name);
                  } else {
                    deleteUserPermanent(showDeleteConfirm.id, showDeleteConfirm.name);
                  }
                }}
                className="flex-1 py-2 bg-red-600 rounded-lg hover:bg-red-700"
              >
                Slett permanent
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Edit Organization Modal */}
      {showEditOrg && (
        <Modal title="Rediger organisasjon" onClose={() => setShowEditOrg(false)}>
          <form onSubmit={updateOrganization} className="space-y-4">
            <Input label="Organisasjonsnavn" value={editOrg.name} onChange={e => setEditOrg({...editOrg, name: e.target.value})} required />
            <Input label="Org.nummer" value={editOrg.orgnr} onChange={e => setEditOrg({...editOrg, orgnr: e.target.value})} required />
            
            <div className="flex items-center gap-3">
              <input type="checkbox" id="edit_test_org" checked={editOrg.is_test_org} onChange={e => setEditOrg({...editOrg, is_test_org: e.target.checked})} className="rounded" />
              <label htmlFor="edit_test_org" className="text-sm">Testorganisasjon (tidsbegrenset)</label>
            </div>
            
            {editOrg.is_test_org && (
              <Input label="Utløper om (dager)" type="number" value={editOrg.expires_days} onChange={e => setEditOrg({...editOrg, expires_days: parseInt(e.target.value)})} min={1} max={365} />
            )}
            
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={() => setShowEditOrg(false)} className="flex-1 py-2 border border-gray-600 rounded-lg hover:bg-gray-700">Avbryt</button>
              <button type="submit" className="flex-1 py-2 bg-blue-600 rounded-lg hover:bg-blue-700">Lagre</button>
            </div>
          </form>
        </Modal>
      )}

      {/* Edit User Modal */}
      {showEditUser && (
        <Modal title="Rediger bruker" onClose={() => setShowEditUser(false)}>
          <form onSubmit={updateUser} className="space-y-4">
            <Input label="Navn" value={editUser.name} onChange={e => setEditUser({...editUser, name: e.target.value})} required />
            <Input label="E-post" type="email" value={editUser.email} onChange={e => setEditUser({...editUser, email: e.target.value})} required />
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Rolle</label>
              <select
                value={editUser.role}
                onChange={e => setEditUser({...editUser, role: e.target.value})}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
              >
                <option value="Member">Medlem</option>
                <option value="PowerUser">Superbruker</option>
                <option value="Admin">Administrator</option>
              </select>
            </div>
            
            <div className="flex items-center gap-3">
              <input type="checkbox" id="edit_test_user" checked={editUser.is_test_user} onChange={e => setEditUser({...editUser, is_test_user: e.target.checked})} className="rounded" />
              <label htmlFor="edit_test_user" className="text-sm">Testbruker (tidsbegrenset)</label>
            </div>
            
            {editUser.is_test_user && (
              <Input label="Utløper om (dager)" type="number" value={editUser.expires_days} onChange={e => setEditUser({...editUser, expires_days: parseInt(e.target.value)})} min={1} max={365} />
            )}
            
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={() => setShowEditUser(false)} className="flex-1 py-2 border border-gray-600 rounded-lg hover:bg-gray-700">Avbryt</button>
              <button type="submit" className="flex-1 py-2 bg-blue-600 rounded-lg hover:bg-blue-700">Lagre</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

// Helper Components are now in admin/shared.js

// Payments Tab Component
export default SystemAdminPanel;
